import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { CreateSupplierModal } from "@/pages/stammdaten/CreateSupplierModal";
import { EditSupplierModal } from "@/pages/stammdaten/EditSupplierModal";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import type { Supplier } from "@/types/camping";

export function SuppliersPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteStammdaten(groups);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Supplier | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<Supplier[]>("/api/v1/suppliers?include_inactive=true")
      .then(setSuppliers)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "Lieferanten" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-label-secondary">
        Lieferanten können Artikeln zugeordnet werden und dienen als Stammdaten für Einkauf und
        Wareneingang.
      </p>

      <EditSupplierModal
        open={!!selected}
        supplier={selected}
        onClose={() => setSelected(null)}
        onSaved={load}
        canEdit={canWrite}
      />
      <CreateSupplierModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      {!canWrite ? <StammdatenWriteHint /> : null}

      {canWrite ? (
        <div className="flex justify-end">
          <Button type="button" className="w-full sm:w-auto" onClick={() => setCreateOpen(true)}>
            Neuer Lieferant
          </Button>
        </div>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <Table wide emptyMessage="Keine Lieferanten." isEmpty={suppliers.length === 0}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Kontakt</th>
              <th>E-Mail</th>
              <th>Telefon</th>
              <th>Bestellungen</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((supplier) => (
              <ClickableTableRow
                key={supplier.id}
                ariaLabel={`Lieferant ${supplier.name} öffnen`}
                onActivate={() => setSelected(supplier)}
              >
                <td className="font-medium">{supplier.name}</td>
                <td className="text-sm text-label-secondary">
                  {supplier.contact_person ?? "—"}
                </td>
                <td className="text-sm text-label-secondary">{supplier.email ?? "—"}</td>
                <td className="text-sm text-label-secondary">{supplier.phone ?? "—"}</td>
                <td className="text-sm text-label-secondary">
                  {supplier.orders_enabled ? "Aktiv" : "—"}
                </td>
                <td>{supplier.is_active ? "Aktiv" : "Inaktiv"}</td>
              </ClickableTableRow>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
